import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eyebrow, Reveal } from "@/components/SectionHeader";
import { Check, ArrowUpRight, Terminal, Webhook, TestTube2, LifeBuoy } from "lucide-react";

const CODE = `// One integration. Every corridor.
import Pay10x from "@pay10x/sdk";

const client = new Pay10x({ apiKey: process.env.PAY10X_KEY });

const payment = await client.payments.create({
  amount:   { currency: "USD", value: 12500 },
  payout:   { method: "bank",  country: "PH" },
  reference:"INV-2451",
  metadata: { corridor: "US-PH", channel: "web" },
});

// Real-time status via webhook or SSE
client.on("payment.status", (evt) => {
  console.log(evt.status, evt.network, evt.settledAt);
  // "SETTLED" · "PAYNOW" · "2025-11-04T09:12:44Z"
});`;

const FEATURES = [
  { icon: TestTube2, t: "Sandbox access to test integrations before going live" },
  { icon: Terminal,  t: "Clear, consistent documentation across networks and methods" },
  { icon: Webhook,   t: "Webhooks and real-time callbacks for status and settlement" },
  { icon: LifeBuoy,  t: "Structured onboarding with dedicated technical support" },
];

export default function Developers() {
  return (
    <div className="bg-[color:var(--p10-ink)] text-white">
      <section className="pt-40 pb-20 md:pb-24 relative border-b border-white/10">
        <div className="grain" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Eyebrow dark>Developers</Eyebrow>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.98] mt-6"
            >
              Build on <em className="text-[color:var(--p10-warm)]">Pay10x</em>.
            </motion.h1>
            <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-lg">
              One API. Multiple networks. Real corridors. From first call to production in
              weeks — not months.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link data-testid="dev-cta-docs" to="/contact" className="btn-square btn-electric">
                View API docs <ArrowUpRight className="ml-2 w-4 h-4" />
              </Link>
              <Link data-testid="dev-cta-sandbox" to="/contact" className="btn-square btn-ghost-dark">
                Request sandbox access
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="rounded-none border border-white/10 bg-[color:var(--p10-ink-2)] shadow-2xl">
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                <div className="flex gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                  <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                </div>
                <div className="font-mono-tech text-[10px] text-white/40 tracking-[0.2em]">
                  pay10x.node.js
                </div>
              </div>
              <pre className="font-mono-tech text-[12px] leading-[1.7] text-white/85 p-6 overflow-x-auto whitespace-pre">
{CODE}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow dark>What you get</Eyebrow>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.02] mt-6">
                Everything a payments team <em className="text-[color:var(--p10-warm)]">actually needs</em>.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <ul>
                {FEATURES.map((f, i) => (
                  <Reveal key={i} delay={i * 0.08}>
                    <li className="flex items-start gap-6 py-6 border-b border-white/10">
                      <span className="w-10 h-10 border border-[color:var(--p10-electric)]/40 bg-[color:var(--p10-electric)]/10 flex items-center justify-center shrink-0">
                        <f.icon className="w-5 h-5 text-[color:var(--p10-electric)]" />
                      </span>
                      <span className="text-xl md:text-2xl font-serif-display text-white">
                        {f.t}
                      </span>
                    </li>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CHECKLIST + CTA */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { n: "Week 1", t: "Sandbox onboarding, credentials issued." },
              { n: "Week 2-3", t: "Integration + certification with corridor-specific tests." },
              { n: "Week 4", t: "Go live. Real-time monitoring and dedicated support." },
            ].map((s, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="p-10 border border-white/10 bg-[color:var(--p10-ink-2)]"
              >
                <div className="font-mono-tech text-xs tracking-[0.2em] text-[color:var(--p10-electric)]">
                  {s.n}
                </div>
                <p className="mt-6 font-serif-display text-2xl leading-tight text-white">
                  {s.t}
                </p>
                <Check className="w-5 h-5 text-[color:var(--p10-electric)] mt-6" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}